import React, { useState } from 'react';
import classnames from 'classnames';
import { useValueChange } from '@leverege/ui-hooks';
import { IdGen, Pane, Label, NumericInput, Checkbox, Popup, Text, TabPane, TextInput } from '@leverege/ui-elements';
import { Colorizers } from '@leverege/ui-attributes';
import { ModelTypeSelector } from '@leverege/ui-plugin';
import { HistoryModel as Model, Symbolizers, HistoryAccumulators, LineStyleSelector, CapSelector, JoinSelector } from '@leverege/molten/data-viewers';
import S from './Grid.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function CaseHistoryEditor(props) {
  const {
    value
  } = props;
  const onModelChange = useValueChange(Model, props);
  const [ids] = useState(IdGen.createIds('attr', 15));
  let cid = 0;
  if (value == null) {
    return null;
  }
  const scalesWithZoom = Model.getScalesWithZoom(value);
  const objectType = Model.getObjectType(value);
  return /*#__PURE__*/_jsxs(TabPane, {
    children: [/*#__PURE__*/_jsx(TabPane.Tab, {
      tabId: "visibility",
      tabName: "Visibility",
      children: /*#__PURE__*/_jsxs(Pane, {
        variant: "editor",
        className: S.grid,
        children: [/*#__PURE__*/_jsx(Checkbox, {
          id: ids[cid++],
          className: S.fill,
          label: "Visible",
          eventData: "setVisible",
          value: Model.getVisible(value),
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(Text, {
          className: S.fill,
          variant: "subtitle",
          children: "Zoom"
        }), /*#__PURE__*/_jsxs("div", {
          className: classnames(S.sub, S.grid),
          children: [/*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: "Show At Zoom "
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            eventData: "setShowAtZoom",
            min: 0,
            max: Math.min(25, Model.getHideAtZoom(value)),
            allowError: true,
            value: Model.getShowAtZoom(value),
            onChange: onModelChange
          }, `haz-${Model.getHideAtZoom(value) || 'n'}`), /*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: "Hide At Zoom"
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            min: Math.max(0, Model.getShowAtZoom(value)),
            max: 25,
            eventData: "setHideAtZoom",
            allowError: true,
            value: Model.getHideAtZoom(value),
            onChange: onModelChange
          }, `saz-${Model.getShowAtZoom(value) || 'n'}`), /*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: "Fade In/Out Zoom"
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            min: 0,
            max: 10,
            eventData: "setFadeZoom",
            allowError: true,
            float: true,
            value: Model.getFadeZoom(value),
            onChange: onModelChange
          })]
        }), /*#__PURE__*/_jsx(Popup, {
          title: "Help",
          icon: "fa fa-question-circle fa-fw",
          children: /*#__PURE__*/_jsxs("div", {
            className: S.pad,
            children: [/*#__PURE__*/_jsx(Text, {
              className: classnames(S.fill, S.hint),
              variant: "caption",
              children: "Setting a Show and Hide zoom level allows the layer to be visible only at certain zoom levels."
            }), /*#__PURE__*/_jsx(Text, {
              className: S.fill,
              variant: "subtitle",
              children: "Zoom Level"
            }), /*#__PURE__*/_jsxs("div", {
              className: S.grid,
              style: {
                columnGap: 'var( --size-padXLarge, 32px)'
              },
              children: [/*#__PURE__*/_jsx("span", {
                children: "0"
              }), /*#__PURE__*/_jsx("span", {
                children: "The Earth"
              }), /*#__PURE__*/_jsx("span", {
                children: "3"
              }), /*#__PURE__*/_jsx("span", {
                children: "A continent"
              }), /*#__PURE__*/_jsx("span", {
                children: "4"
              }), /*#__PURE__*/_jsx("span", {
                children: "Large islands"
              }), /*#__PURE__*/_jsx("span", {
                children: "6"
              }), /*#__PURE__*/_jsx("span", {
                children: "Large rivers"
              }), /*#__PURE__*/_jsx("span", {
                children: "10"
              }), /*#__PURE__*/_jsx("span", {
                children: "Large Roads"
              }), /*#__PURE__*/_jsx("span", {
                children: "15"
              }), /*#__PURE__*/_jsx("span", {
                children: "Buildings"
              }), /*#__PURE__*/_jsx("span", {
                children: "18"
              }), /*#__PURE__*/_jsx("span", {
                children: "A Vehicle"
              })]
            })]
          })
        })]
      })
    }), /*#__PURE__*/_jsx(TabPane.Tab, {
      tabId: "appearance",
      tabName: "Appearance",
      children: /*#__PURE__*/_jsxs(Pane, {
        variant: "editor",
        className: S.grid,
        children: [/*#__PURE__*/_jsx(Text, {
          className: S.fill,
          variant: "subtitle",
          children: "Line Style"
        }), /*#__PURE__*/_jsxs("div", {
          className: classnames(S.grid, S.fill, S.sub),
          children: [/*#__PURE__*/_jsx(Label, {
            htmlFor: ids[cid],
            children: "Width"
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            min: 0,
            max: 50,
            float: true,
            eventData: "setLineWidth",
            allowError: true,
            value: Model.getLineWidth(value),
            onChange: onModelChange
          }), /*#__PURE__*/_jsx(Label, {
            htmlFor: ids[cid],
            children: "Color"
          }), ModelTypeSelector.createSelectorAndEditor({
            editorProps: {
              id: ids[cid++]
            },
            modelKey: 'colorizer',
            values: Colorizers.getColorizersFor(objectType),
            value: Model.getLineColor(value),
            eventData: 'setLineColor',
            onChange: onModelChange
          }, S.fill), /*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: "Opacity"
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            min: 0,
            max: 1,
            float: true,
            eventData: "setLineOpacity",
            allowError: true,
            value: Model.getLineOpacity(value),
            onChange: onModelChange
          }), /*#__PURE__*/_jsx(Label, {
            htmlFor: ids[cid],
            children: "Style"
          }), /*#__PURE__*/_jsx(LineStyleSelector, {
            id: ids[cid++],
            value: Model.getLineStyle(value),
            eventData: "setLineStyle",
            onChange: onModelChange
          }), Model.getLineStyle(value) === 'pattern' && /*#__PURE__*/_jsxs(React.Fragment, {
            children: [/*#__PURE__*/_jsx(Label, {
              variant: "input",
              htmlFor: ids[cid],
              children: "Pattern"
            }), /*#__PURE__*/_jsx(TextInput, {
              id: ids[cid++],
              eventData: "setLinePattern",
              allowError: true,
              value: Model.getLinePattern(value),
              onChange: onModelChange
            })]
          }), /*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: "Cap"
          }), /*#__PURE__*/_jsx(CapSelector, {
            eventData: "setLineCap",
            value: Model.getLineCap(value) || 'butt',
            onChange: onModelChange
          }), /*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: "Join"
          }), /*#__PURE__*/_jsx(JoinSelector, {
            eventData: "setLineJoin",
            value: Model.getLineJoin(value) || 'miter',
            onChange: onModelChange
          })]
        }), /*#__PURE__*/_jsx(Text, {
          className: S.fill,
          variant: "subtitle",
          children: "Symbolizer"
        }), /*#__PURE__*/_jsxs("div", {
          className: classnames(S.grid, S.fill, S.sub),
          children: [/*#__PURE__*/_jsx(Label, {
            htmlFor: ids[cid],
            children: "Symbol"
          }), ModelTypeSelector.createSelectorAndEditor({
            editorProps: {
              id: ids[cid++]
            },
            modelKey: 'symbolizer',
            values: Symbolizers.getSymbolizersFor(objectType),
            value: Model.getSymbolizer(value),
            eventData: 'setSymbolizer',
            onChange: onModelChange
          }, S.fill)]
        }), /*#__PURE__*/_jsx(Text, {
          className: S.fill,
          variant: "subtitle",
          children: "Scale"
        }), /*#__PURE__*/_jsxs("div", {
          className: classnames(S.grid, S.fill, S.sub),
          children: [/*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: "Normal"
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            min: 0.1,
            max: 10,
            float: true,
            eventData: "setNormalScale",
            value: Model.getNormalScale(value),
            onChange: onModelChange
          }), /*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: "Rollover"
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            min: 0.1,
            max: 10,
            float: true,
            eventData: "setRolloverScale",
            value: Model.getRolloverScale(value),
            onChange: onModelChange
          }), /*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: "Selected"
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            min: 0.1,
            max: 10,
            float: true,
            eventData: "setSelectedScale",
            value: Model.getSelectedScale(value),
            onChange: onModelChange
          }), /*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: "Alert"
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            min: 0.1,
            max: 10,
            float: true,
            eventData: "setAlertScale",
            value: Model.getAlertScale(value),
            onChange: onModelChange
          }), /*#__PURE__*/_jsx(Checkbox, {
            id: ids[cid++],
            className: S.fill,
            label: "Scales with Zoom",
            eventData: "setScalesWithZoom",
            value: scalesWithZoom,
            onChange: onModelChange
          }), scalesWithZoom && /*#__PURE__*/_jsxs("div", {
            className: classnames(S.grid, S.fill, S.sub),
            children: [/*#__PURE__*/_jsxs("div", {
              className: S.grid,
              children: [/*#__PURE__*/_jsx(Label, {
                variant: "input",
                htmlFor: ids[cid],
                children: "From Zoom"
              }), /*#__PURE__*/_jsx(NumericInput, {
                id: ids[cid++],
                min: 0.1,
                max: 10,
                float: true,
                eventData: "setScaleFromZoom",
                value: Model.getScaleFromZoom(value),
                onChange: onModelChange
              }), /*#__PURE__*/_jsx(Label, {
                variant: "input",
                htmlFor: ids[cid],
                children: "To Zoom"
              }), /*#__PURE__*/_jsx(NumericInput, {
                id: ids[cid++],
                min: 0.1,
                max: 10,
                float: true,
                eventData: "setScaleToZoom",
                value: Model.getScaleToZoom(value),
                onChange: onModelChange
              })]
            }), /*#__PURE__*/_jsxs("div", {
              className: S.grid,
              children: [/*#__PURE__*/_jsx(Label, {
                variant: "input",
                htmlFor: ids[cid],
                children: "Factor"
              }), /*#__PURE__*/_jsx(NumericInput, {
                id: ids[cid++],
                min: 0,
                max: 10,
                float: true,
                eventData: "setScaleFromFactor",
                value: Model.getScaleFromFactor(value),
                onChange: onModelChange
              }), /*#__PURE__*/_jsx(Label, {
                variant: "input",
                htmlFor: ids[cid],
                children: "Factor"
              }), /*#__PURE__*/_jsx(NumericInput, {
                id: ids[cid++],
                min: 0,
                max: 10,
                float: true,
                eventData: "setScaleToFactor",
                value: Model.getScaleToFactor(value),
                onChange: onModelChange
              })]
            })]
          })]
        })]
      })
    }), /*#__PURE__*/_jsx(TabPane.Tab, {
      tabId: "interaction",
      tabName: "Interaction",
      children: /*#__PURE__*/_jsxs(Pane, {
        variant: "editor",
        className: S.grid,
        children: [/*#__PURE__*/_jsx(Checkbox, {
          id: ids[cid++],
          className: S.fill,
          label: "Rollover",
          eventData: "setRolloverable",
          value: Model.getRolloverable(value),
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(Checkbox, {
          id: ids[cid++],
          className: S.fill,
          label: "Selectable",
          eventData: "setSelectable",
          value: Model.getSelectable(value),
          onChange: onModelChange
        })]
      })
    }), /*#__PURE__*/_jsx(TabPane.Tab, {
      tabId: "query",
      tabName: "Query",
      children: /*#__PURE__*/_jsxs(Pane, {
        variant: "editor",
        className: S.grid,
        children: [/*#__PURE__*/_jsxs(Text, {
          className: S.fill,
          children: [/*#__PURE__*/_jsx("b", {
            children: "Note:\xA0"
          }), "Query range is based on case start and end dates"]
        }), /*#__PURE__*/_jsx(Label, {
          htmlFor: ids[cid],
          children: "Max Points"
        }), /*#__PURE__*/_jsx(NumericInput, {
          id: ids[cid++],
          eventData: "setPerPage",
          value: Model.getPerPage(value),
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(Label, {
          children: "Accumulator"
        }), ModelTypeSelector.createSelectorAndEditor({
          editorProps: {
            id: ids[cid++]
          },
          modelKey: 'accumulator',
          values: HistoryAccumulators.getAccumulatorsFor(objectType),
          value: Model.getAccumulator(value),
          eventData: 'setAccumulator',
          onChange: onModelChange
        }, S.fill)]
      })
    })]
  });
}
export default /*#__PURE__*/React.memo(CaseHistoryEditor);